import { useEffect, useState } from 'react';
import type { ToolExecutionHar } from '@terra/shared-contracts-tracker';
import type { ToolHarSummary } from './tool-call-update-header-metadata';

export function summarizeHarEntries(
  hars: ToolExecutionHar[],
): ToolHarSummary | null {
  if (hars.length === 0) {
    return null;
  }

  if (hars.length === 1) {
    return { count: 1, duration: hars[0].harEntry.time };
  }

  const startTime = new Date(hars[0].timestamp).getTime();
  const endTime = new Date(hars[hars.length - 1].timestamp).getTime();
  const lastHarDuration = hars[hars.length - 1].harEntry.time;

  return {
    count: hars.length,
    duration: endTime - startTime + lastHarDuration,
  };
}

export function useToolHarSummary({
  enabled,
  toolCallId,
  fetchHarData,
}: {
  enabled: boolean;
  toolCallId: string | undefined;
  fetchHarData: (id: string) => Promise<ToolExecutionHar[]>;
}): ToolHarSummary | null {
  const [harSummary, setHarSummary] = useState<ToolHarSummary | null>(null);

  useEffect(() => {
    if (!enabled || !toolCallId) {
      setHarSummary(null);

      return;
    }

    let cancelled = false;

    fetchHarData(toolCallId)
      .then((hars) => {
        if (!cancelled) {
          setHarSummary(summarizeHarEntries(hars));
        }
      })
      .catch((error: unknown) => {
        console.warn('Failed to load HAR summary; continuing without it', error);

        if (!cancelled) {
          setHarSummary(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, toolCallId, fetchHarData]);

  return harSummary;
}
